import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Home,
  Store,
  Eye,
  Plus,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Image,
  Tag,
  MapPin,
  Package,
  Trash2,
  Home as HomeIcon,
  CreditCard,
  Building2,
  Users,
} from "lucide-react";
import {
  clearCache,
  clearHomepageCache,
} from "../../store/slices/utilitySlice";
const logo = new URL(`../../assets/images/logo.png`, import.meta.url);

const Sidebar = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.utility);

  // Check if any child item is active to determine if parent should be expanded
  const isChildActive = (children) => {
    if (!children) return false;
    return children.some((child) => {
      if (child.href === location.pathname) return true;
      if (child.children) return isChildActive(child.children);
      return false;
    });
  };

  const navigationItems = [
    { id: "dashboard", label: "Dashboard", icon: Home, href: "/" },
    {
      id: "shop",
      label: "Business",
      icon: Store,
      children: [
        { id: "view-shops", label: "View Business", icon: Eye, href: "/business" },
        { id: "add-shop", label: "Add Business", icon: Plus, href: "/business/add" },
      ],
    },
    {
      id: "customers",
      label: "Customers",
      icon: Users,
      href: "/customers",
    },
    {
      id: "categories",
      label: "Categories",
      icon: Tag,
      children: [
        { 
          id: "categories-group",
          label: "Categories",
          icon: Tag,
          isGroup: true,
          children: [
            { id: "view-categories", label: "View Categories", icon: Eye, href: "/categories" },
            { id: "add-category", label: "Add Category", icon: Plus, href: "/categories/add" },
          ]
        },
        { 
          id: "subcategories-group",
          label: "SubCategories",
          icon: Tag,
          isGroup: true,
          children: [
            { id: "view-subcategories", label: "View SubCategories", icon: Eye, href: "/sub-categories" },
            { id: "add-subcategory", label: "Add SubCategory", icon: Plus, href: "/sub-categories/add" },
          ]
        },
        { 
          id: "services-group",
          label: "Services",
          icon: Tag,
          isGroup: true,
          children: [
            { id: "view-services", label: "View Services", icon: Eye, href: "/category-services" },
            { id: "add-service", label: "Add Service", icon: Plus, href: "/category-services/add" },
          ]
        },
      ],
    },
    {
      id: "locations",
      label: "Locations",
      icon: MapPin,
      children: [
        { 
          id: "states-group",
          label: "States",
          icon: MapPin,
          isGroup: true,
          children: [
            { id: "view-states", label: "View States", icon: Eye, href: "/states" },
            { id: "add-state", label: "Add State", icon: Plus, href: "/states/add" },
          ]
        },
        { 
          id: "districts-group",
          label: "Districts",
          icon: MapPin,
          isGroup: true,
          children: [
            { id: "view-districts", label: "View Districts", icon: Eye, href: "/districts" },
            { id: "add-district", label: "Add District", icon: Plus, href: "/districts/add" },
          ]
        },
        { 
          id: "talukas-group",
          label: "Talukas",
          icon: MapPin,
          isGroup: true,
          children: [
            { id: "view-talukas", label: "View Talukas", icon: Eye, href: "/talukas" },
            { id: "add-taluka", label: "Add Taluka", icon: Plus, href: "/talukas/add" },
          ]
        },
      ],
    },
    {
      id: "banner",
      label: "Banner",
      icon: Image,
      children: [
        { id: "view-banners", label: "View Banners", icon: Eye, href: "/banners" },
        { id: "add-banner", label: "Add Banner", icon: Plus, href: "/banners/add" },
      ],
    },
    {
      id: "subscription-packages",
      label: "Subscription Packages",
      icon: Package,
      children: [
        { id: "view-packages", label: "View Packages", icon: Eye, href: "/subscription-packages" },
        { id: "add-package", label: "Add Package", icon: Plus, href: "/subscription-packages/add" },
      ],
    },
    {
      id: "payment-modes",
      label: "Payment Modes",
      icon: CreditCard,
      children: [
        { id: "view-payment-modes", label: "View Payment Modes", icon: Eye, href: "/payment-modes" },
        { id: "add-payment-mode", label: "Add Payment Mode", icon: Plus, href: "/payment-modes/add" },
      ],
    },
    {
      id: "company-details",
      label: "Company Details",
      icon: Building2,
      href: "/company-details",
    },
  ];

  // Initialize expanded items based on active child items
  const getInitialExpandedItems = () => {
    const expanded = [];
    const checkAndExpand = (items, parentId = null) => {
      items.forEach((item) => {
        if (item.children) {
          if (isChildActive(item.children)) {
            expanded.push(item.id);
            if (parentId) expanded.push(parentId);
          }
          checkAndExpand(item.children, item.id);
        }
      });
    };
    checkAndExpand(navigationItems);
    return expanded;
  };

  const [expandedItems, setExpandedItems] = useState(() => getInitialExpandedItems());

  // Update expanded items when location changes
  useEffect(() => {
    setExpandedItems(getInitialExpandedItems());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const toggleExpanded = (itemId) => {
    setExpandedItems((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleItemClick = (item) => {
    if (item.children) {
      toggleExpanded(item.id);
    } else if (item.href) {
      navigate(item.href);
      // Close sidebar on mobile after navigation
      if (onToggle) {
        onToggle();
      }
    }
  };

  const handleChildClick = (child) => {
    if (child.children) {
      toggleExpanded(child.id);
    } else if (child.href) {
      navigate(child.href);
      // Close sidebar on mobile after navigation
      if (onToggle) {
        onToggle();
      }
    }
  };

  return (
    <>
      {/* Sidebar */}
      <aside
        className={`theme-sidebar fixed lg:static inset-y-0 left-0 z-40 w-64 transform transition-transform duration-300 ease-in-out ${
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Logo/Brand */}
          <div className="flex items-center justify-between h-[85px] px-6 border-b border-sidebar-border">
            <div
              className="flex items-center space-x-3 cursor-pointer"
              onClick={() => navigate("/")}
            >
              <img src={logo} alt="Logo" className="w-32" />
            </div>
            <button
              onClick={onToggle}
              className="lg:hidden p-2 rounded-md bg-card-bg border border-card-border text-text-primary hover:bg-surface-50 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 overflow-y-auto scrollbar-hide">
            <ul className="space-y-2">
              {navigationItems.map((item) => {
                const IconComponent = item.icon;
                const isActive = location.pathname === item.href;
                const hasChildren = item.children && item.children.length > 0;
                const isExpanded = expandedItems.includes(item.id);

                return (
                  <li key={item.id}>
                    <button
                      onClick={() => handleItemClick(item)}
                      className={`w-full flex items-center justify-between px-4 py-3 text-left rounded-lg transition-colors duration-200 ${
                        isActive && !hasChildren
                          ? "theme-sidebar-active"
                          : "text-text-secondary hover:bg-surface-50 hover:text-text-primary"
                      }`}
                    >
                      <div className="flex items-center space-x-3">
                        <IconComponent
                          className={`h-5 w-5 ${
                            isActive && !hasChildren
                              ? "text-white"
                              : "text-text-tertiary"
                          }`}
                        />
                        <span className="font-medium">{item.label}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        {hasChildren ? (
                          <ChevronDown
                            className={`h-4 w-4 text-text-tertiary transform transition-transform duration-200 ${
                              isExpanded ? "rotate-180" : ""
                            }`}
                          />
                        ) : (
                          <ChevronRight
                            className={`h-4 w-4 text-text-tertiary ${
                              isActive ? "text-white" : ""
                            }`}
                          />
                        )}
                      </div>
                    </button>

                    {/* Submenu */}
                    {hasChildren && (
                      <div
                        className={`ml-4 mt-2 overflow-hidden transition-all duration-200 ${
                          isExpanded
                            ? "max-h-[600px] opacity-100"
                            : "max-h-0 opacity-0"
                        }`}
                      >
                        <ul className="space-y-1 border-l-2 border-border-light p-2">
                          {item.children.map((child) => {
                            const ChildIconComponent = child.icon;
                            const isChildActive = location.pathname === child.href;
                            const hasNestedChildren = child.children && child.children.length > 0;
                            const isChildExpanded = expandedItems.includes(child.id);

                            return (
                              <li key={child.id} className="m-2">
                                <button
                                  onClick={() => handleChildClick(child)}
                                  className={`w-full flex items-center justify-between px-3 py-2 text-left rounded-md transition-colors duration-200 ${
                                    isChildActive && !hasNestedChildren
                                      ? "theme-sidebar-active"
                                      : "text-text-secondary hover:bg-surface-50 hover:text-text-primary"
                                  }`}
                                >
                                  <div className="flex items-center space-x-3">
                                    <ChildIconComponent
                                      className={`h-4 w-4 ${
                                        isChildActive && !hasNestedChildren
                                          ? "text-white"
                                          : "text-text-tertiary"
                                      }`}
                                    />
                                    <span className="text-sm font-medium">
                                      {child.label}
                                    </span>
                                  </div>
                                  {hasNestedChildren && (
                                    <ChevronDown
                                      className={`h-3 w-3 text-text-tertiary transform transition-transform duration-200 ${
                                        isChildExpanded ? "rotate-180" : ""
                                      }`}
                                    />
                                  )}
                                </button>

                                {/* Nested Submenu */}
                                {hasNestedChildren && (
                                  <div
                                    className={`ml-4 mt-1 overflow-hidden transition-all duration-200 ${
                                      isChildExpanded
                                        ? "max-h-96 opacity-100"
                                        : "max-h-0 opacity-0"
                                    }`}
                                  >
                                    <ul className="space-y-1 border-l-2 border-border-light pl-2">
                                      {child.children.map((nestedChild) => {
                                        const NestedIconComponent = nestedChild.icon;
                                        const isNestedActive =
                                          location.pathname === nestedChild.href;

                                        return (
                                          <li key={nestedChild.id} className="m-1">
                                            <button
                                              onClick={() => {
                                                navigate(nestedChild.href);
                                                if (onToggle) onToggle();
                                              }}
                                              className={`w-full flex items-center space-x-2 px-2 py-1.5 text-left rounded-md transition-colors duration-200 ${
                                                isNestedActive
                                                  ? "theme-sidebar-active"
                                                  : "text-text-secondary hover:bg-surface-50 hover:text-text-primary"
                                              }`}
                                            >
                                              <NestedIconComponent
                                                className={`h-3 w-3 ${
                                                  isNestedActive
                                                    ? "text-white"
                                                    : "text-text-tertiary"
                                                }`}
                                              />
                                              <span className="text-xs font-medium">
                                                {nestedChild.label}
                                              </span>
                                            </button>
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Cache Actions */}
          <div className="px-4 py-3 border-t border-sidebar-border space-y-2">
            <button
              onClick={() => dispatch(clearCache())}
              disabled={loading}
              className="w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg bg-red-500 hover:bg-red-600 disabled:bg-red-300 text-white text-sm font-medium transition-colors"
              title="Clear Cache"
            >
              <Trash2 className="h-4 w-4" />
              <span>Clear Cache</span>
            </button>

            <button
              onClick={() => dispatch(clearHomepageCache())}
              disabled={loading}
              className="w-full flex items-center space-x-3 px-4 py-2.5 rounded-lg bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white text-sm font-medium transition-colors"
              title="Clear Homepage Cache"
            >
              <HomeIcon className="h-4 w-4" />
              <span>Clear Homepage</span>
            </button>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-sidebar-border">
            <div className="text-xs text-text-tertiary text-center">
              © 2025 Connectri
            </div>
          </div>
        </div>
      </aside>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/50 z-30 lg:hidden"
            onClick={onToggle}
          />
        </>
      )}
    </>
  );
};

export default Sidebar;
